import React, { useEffect, useState } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { useIntl } from 'react-intl'

type Props = {
    show: boolean
    onHide: () => void
    onSubmit: (reason: string) => void
    actionType: 'blacklist' | 'whitelist' | 'enable' | 'disable'
    entityType: 'url' | 'ip' | 'hop' | 'kyc' | 'sender'
}

const RejectionReasonModal: React.FC<Props> = ({ show, onHide, onSubmit, actionType, entityType }) => {
    const intl = useIntl()
    const [reason, setReason] = useState('')
    const [error, setError] = useState('')

    useEffect(() => {
        if (!show) {
            setReason('')
            setError('')
        }
    }, [show])

    const handleSubmit = () => {
        if (!reason.trim()) {
            setError(intl.formatMessage({ id: 'REJECTION.REASON_REQUIRED' }))
            return
        }
        onSubmit(reason.trim())
    }

    const getEntityLabel = () => {
        if (entityType === 'url') return intl.formatMessage({ id: 'REJECTION.ENTITY.URL' })
        if (entityType === 'hop') return intl.formatMessage({ id: 'REJECTION.ENTITY.HOP' })
        if (entityType === 'kyc') return intl.formatMessage({ id: 'REJECTION.ENTITY.KYC' })
        if (entityType === 'sender') return intl.formatMessage({ id: 'REJECTION.ENTITY.SENDER' })
        return intl.formatMessage({ id: 'REJECTION.ENTITY.IP' })
    }

    const getActionVerb = (gerund = false) => {
        if (entityType === 'hop' || entityType === 'kyc' || entityType === 'sender') {
            if (actionType === 'whitelist') return gerund
                ? intl.formatMessage({ id: 'REJECTION.ACTION.APPROVING' })
                : intl.formatMessage({ id: 'REJECTION.ACTION.APPROVE' })
            if (actionType === 'blacklist') return gerund
                ? intl.formatMessage({ id: 'REJECTION.ACTION.REJECTING' })
                : intl.formatMessage({ id: 'REJECTION.ACTION.REJECT' })
        }
        if (actionType === 'blacklist') return gerund
            ? intl.formatMessage({ id: 'REJECTION.ACTION.BLACKLISTING' })
            : intl.formatMessage({ id: 'REJECTION.ACTION.BLACKLIST' })
        if (actionType === 'whitelist') return gerund
            ? intl.formatMessage({ id: 'REJECTION.ACTION.WHITELISTING' })
            : intl.formatMessage({ id: 'REJECTION.ACTION.WHITELIST' })
        if (actionType === 'disable') return gerund
            ? intl.formatMessage({ id: 'REJECTION.ACTION.DISABLING' })
            : intl.formatMessage({ id: 'REJECTION.ACTION.DISABLE' })
        return gerund
            ? intl.formatMessage({ id: 'REJECTION.ACTION.ENABLING' })
            : intl.formatMessage({ id: 'REJECTION.ACTION.ENABLE' })
    }

    const title = intl.formatMessage({ id: 'REJECTION.TITLE' }, { action: getActionVerb(), entity: getEntityLabel() })
    const placeholder = intl.formatMessage({ id: 'REJECTION.PLACEHOLDER' }, { action: getActionVerb(true), entity: getEntityLabel() })
    const buttonLabel = `${getActionVerb()} ${getEntityLabel()}`

    const buttonVariant = {
        blacklist: 'danger',
        whitelist: 'success',
        disable: 'danger',
        enable: 'success',
    }[actionType]

    return (
        <Modal show={show} onHide={onHide} centered size='lg'>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Control
                        as='textarea'
                        rows={3}
                        className='fs-4 fs-sm-4 fs-md-4 fs-lg-2 fs-xl-2'
                        value={reason}
                        maxLength={300}
                        onChange={(e) => {
                            if (e.target.value.length <= 300) {
                                setReason(e.target.value)
                                setError('')
                            }
                        }}
                        placeholder={placeholder}
                    />
                    <div className='d-flex justify-content-between mt-1'>
                        <div>{error && <Form.Text className='text-danger'>{error}</Form.Text>}</div>
                        <Form.Text className='text-muted'>{reason.length}/300</Form.Text>
                    </div>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' onClick={onHide}>
                    {intl.formatMessage({ id: 'COMMON.BUTTON.CANCEL' })}
                </Button>
                <Button variant={buttonVariant} onClick={handleSubmit}>
                    {buttonLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default RejectionReasonModal
